import React, { useState, useEffect } from "react";
import { Select, SelectProps, Tag } from "antd";
import UserService from "../../../services/crud/UserService";
import User from "../../../models/User";
import TspService from "../../../services/crud/TspService";
import Tsp from "../../../models/Tsp";
import Account from "../../../models/Account";
import AccountService from "../../../services/crud/AccountService";
import Resource from "../../../models/Resource";
import ResourceService from "../../../services/crud/ResourceService";
import Plan from "../../../models/Plan";
import PlanService from "../../../services/crud/PlanService";
import HubService from "../../../services/crud/HubService";
import Hub from "../../../models/Hub";
import WhiteLabel from "../../../models/WhiteLabels";
import WhiteLabelsService from "../../../services/crud/WhiteLabelsService";
import { DefaultOptionType } from "antd/lib/select";
import TagService from "../../../services/crud/TagService";
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';
import TagModel from "../../../models/TagModel";

function Users({ valueIndex = "id", ...props }: SelectProps & { valueIndex?: string }) {
  type Type = User;
  const service = UserService;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Type[]>([]);

  useEffect(() => {
    setLoading(true);
    service
      .list()
      .then((data) => setData(data))
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Select
      loading={loading}
      options={data.map((data: Type) => {
        return { label: data.username, value: data[valueIndex] } as DefaultOptionType;
      })}
      {...props}
    ></Select>
  );
}

function Tsps({ valueIndex = "id", onSelect, onSelectTsp, ...props }: SelectProps & { valueIndex?: string, onSelectTsp?: (tspId, tsp: Tsp) => void}) {
  type Type = Tsp;
  const service = TspService;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Type[]>([]);

  useEffect(() => {
    setLoading(true);
    service
      .list()
      .then((data) => {
        data.sort((a, b) => a.name.localeCompare(b.name));
        return setData(data);
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Select
      loading={loading}
      options={data.map((data: Type) => {
        return { label: data.name, value: data[valueIndex] } as DefaultOptionType;
      })}
      onSelect={onSelect || onSelectTsp ? (value, option) => {
        if (onSelectTsp) {
          onSelectTsp(value, data.filter((a) => a.id == value)[0]);
        }
        if (onSelect) {
          onSelect(value, option);
        }
      } : null}
      {...props}
    ></Select>
  );
}

/**
 * @param accountTypes: array of account types eg. ['private', 'company', 'real_estate'] to include.
 */
function Accounts({accountTypes, ...props}: SelectProps & { accountTypes?: string[]}) {
  type Type = Account;
  const service = AccountService;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Type[]>([]);

  useEffect(() => {
    setLoading(true);
    service
      .list()
      .then((data) => {
        if (accountTypes) {
          data = data.filter((a) => accountTypes.includes(a.type));
        }
        data.sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLowerCase()));
        return setData(data);
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Select
      loading={loading}
      options={data.map((data: Type) => {
        return { label: `${Account.typeEmoji(data.type)} ${data.name}`, value: data.id } as DefaultOptionType;
      })}
      {...props}
    ></Select>
  );
}
function Data(props: SelectProps & { data }) {
  return (
    <Select
      options={props.data.map((data) => {
        return { label: data.name, value: data.id } as DefaultOptionType;
      })}
      {...props}
    ></Select>
  );
}
/**
 * @param accountTypes: array of account types eg. ['private', 'company', 'real_estate'] to include.
 * @param onSelectAccount: onSelect variant that gives the loaded Account data of selected account.
 */
function UserAccounts({accountTypes, onSelect, onSelectAccount, ...props}: SelectProps & { userId, accountTypes?: string[], onSelectAccount?: (accountId, account: Account) => void}) {
  type Type = Account;
  const service = AccountService;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Type[]>([]);

  useEffect(() => {
    setLoading(true);
    service
      .listAccountsOnUser(props.userId)
      .then((data) => {
        if (accountTypes) {
          data = data.filter((a) => accountTypes.includes(a.type));
        }
        data.sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLowerCase()));
        return setData(data);
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.userId]);
  return (
    <Select
      loading={loading}
      options={data.map((data: Type) => {
        return { label: `${Account.typeEmoji(data.type)} ${data.name}`, value: data.id } as DefaultOptionType;
      })}
      onSelect={onSelect || onSelectAccount ? (value, option) => {
        if (onSelectAccount) {
          onSelectAccount(value, data.filter((a) => a.id == value)[0]);
        }
        if (onSelect) {
          onSelect(value, option);
        }
      } : null}
      {...props}
    ></Select>
  );
}

function Resources(props: SelectProps) {
  type Type = Resource;
  const service = ResourceService;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Type[]>([]);

  useEffect(() => {
    setLoading(true);
    service
      .list()
      .then((data) => setData(data))
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Select
      loading={loading}
      options={data.map((data: Type) => {
        return { label: data.name, value: data.id } as DefaultOptionType;
      })}
      {...props}
    ></Select>
  );
}

function Plans(props: SelectProps) {
  type Type = Plan;
  const service = PlanService;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Type[]>([]);

  useEffect(() => {
    setLoading(true);
    service
      .list()
      .then((data) => setData(data))
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Select
      loading={loading}
      options={data.map((data: Type) => {
        return { label: data.name, value: data.id } as DefaultOptionType;
      })}
      {...props}
    ></Select>
  );
}

function Hubs(props: SelectProps) {
  type Type = Hub;
  const service = HubService;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Type[]>([]);

  useEffect(() => {
    setLoading(true);
    service
      .list()
      .then((data) => setData(data))
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Select
      loading={loading}
      options={data.map((data: Type) => {
        return { label: data.name, value: data.id } as DefaultOptionType;
      })}
      {...props}
    ></Select>
  );
}

function WhiteLabels(props: SelectProps) {
  type Type = WhiteLabel;
  const service = WhiteLabelsService;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Type[]>([]);
  const options = data.map((data: Type) => {
    return { label: data.name, value: data.id } as DefaultOptionType;
  });
  useEffect(() => {
    setLoading(true);
    service
      .list()
      .then((data) => setData(data))
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <Select loading={loading} defaultValue={null} options={[{ value: null, label: "No White Label" }, ...options]} {...props}></Select>;
}

const segmentTagRender = (props: CustomTagProps & { existingTags: string[] }) => {
  const { label, value, closable, onClose, existingTags } = props;
  const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };
  const isNew = !existingTags.includes(value);
  return (
    <Tag
      color={isNew ? 'grey' : 'blue'}
      onMouseDown={onPreventMouseDown}
      closable={closable}
      onClose={onClose}
      style={{ marginRight: 3 }}
    >
      {label}{isNew ? (<b><i> (new)</i></b>) : <></>}
    </Tag>
  );
};

/**
 * Note that the form data becomes an array and needs to be joined into a comma separated list before sending.
 */
function TagComponent({ valueIndex = "tag", ...props }: SelectProps & { valueIndex?: string }) {
  type Type = TagModel;
  const service = TagService;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Type[]>([]);

  useEffect(() => {
    setLoading(true);
    service
      .list()
      .then((data) => setData(data))
      .finally(() => {
        setLoading(false);
      });
  }, []);
  return (
    <Select
      loading={loading}
      options={data.map((data: Type) => {
        return { label: data.tag, value: data[valueIndex] } as DefaultOptionType;
      })}
      mode="tags"
      tagRender={(props) => segmentTagRender({...props, existingTags: data.map((data: Type) => data.tag)})}
      {...props}
    ></Select>
  );
}

export const EntitySelector = { Users, Tsps, Accounts, Resources, UserAccounts, Data, Plans, Hubs, WhiteLabels, Tag: TagComponent };
